import { DataTable } from "../../components/DataTable/index.tsx";
import { helpers } from "../../util/helpers.ts";
import { getCols } from "../../../common/getCols.ts";
import type { View } from "../../../common/types.ts";
import { Dropdown } from "react-bootstrap";
import {
	wrappedContractAmount,
	wrappedContractExp,
} from "../../components/contract.tsx";
import { wrappedPlayerNameLabels } from "../../components/PlayerNameLabels.tsx";
import type { HandleToggle } from "./index.tsx";
import { range } from "../../../common/utils.ts";
import { SafeHtml } from "../../components/SafeHtml.tsx";
import { useState } from "react";
import clsx from "clsx";
import { PLAYER } from "../../../common/constants.ts";

type HandleBulk = (
	type: "check" | "clear",
	userOrOther: "other" | "user",
	playerOrPick: "pick" | "player",
	draftRoundOnly?: number,
	playerScope?: number[],
) => Promise<void>;

type UserOrOther = "user" | "other";

type TradeProps = View<"trade">;
type Stats = TradeProps["stats"];
type Picks = TradeProps["userRoster"];
type Roster = TradeProps["otherRoster"];

const genPlayerRows = (
	players: Roster,
	handleToggle: HandleToggle,
	userOrOther: UserOrOther,
	stats: Stats,
	challengeNoRatings: boolean,
	tradeContexts: TradeProps["tradeContexts"],
) => {
	return players.map((p) => {
		return {
			key: p.pid,
			data: [
				<input
					className="form-check-input"
					type="checkbox"
					title={p.untradableMsg}
					checked={p.included}
					disabled={p.untradable}
					onChange={() => {
						handleToggle(userOrOther, "player", "include", p.pid);
					}}
				/>,
				<input
					className="form-check-input"
					type="checkbox"
					title={p.untradableMsg ?? "Exclude this player from counter offers"}
					checked={p.excluded || p.untradable}
					disabled={p.untradable}
					onChange={() => {
						handleToggle(userOrOther, "player", "exclude", p.pid);
					}}
				/>,
				wrappedPlayerNameLabels({
					pid: p.pid,
					injury: p.injury,
					jerseyNumber: p.jerseyNumber,
					skills: p.ratings.skills,
					defaultWatch: p.watch,
					firstName: p.firstName,
					firstNameShort: p.firstNameShort,
					lastName: p.lastName,
				}),
				{
					value: (
						<span
							className="small"
							title={
								tradeContexts[p.pid]?.opportunityProtected
									? "Under 21: no demand to leave for lack of promotion"
									: undefined
							}
						>
							{tradeContexts[p.pid]?.traits}{" "}
							{tradeContexts[p.pid]?.traits ? " | " : ""}Mood:{" "}
							{tradeContexts[p.pid]?.label} — {tradeContexts[p.pid]?.reason}
						</span>
					),
					sortValue: tradeContexts[p.pid]?.score ?? 0,
					searchValue: `${tradeContexts[p.pid]?.traits} ${tradeContexts[p.pid]?.label} ${tradeContexts[p.pid]?.reason}`,
				},
				p.ratings.pos,
				p.age,
				!challengeNoRatings ? p.ratings.ovr : null,
				!challengeNoRatings ? p.ratings.pot : null,
				wrappedContractAmount(p),
				wrappedContractExp(p),
				...stats.map((stat) => helpers.roundStat(p.stats[stat], stat)),
			],
			classNames: {
				"table-danger": (p.excluded || p.untradable) && !p.included,
				"table-success": p.included,
			},
		};
	});
};

const genPickRows = (
	picks: Picks,
	handleToggle: HandleToggle,
	userOrOther: UserOrOther,
) => {
	return picks.map((pick) => {
		return {
			key: pick.dpid,
			data: [
				<input
					className="form-check-input"
					name="other-dpids"
					type="checkbox"
					checked={pick.included}
					onChange={() => {
						handleToggle(userOrOther, "pick", "include", pick.dpid);
					}}
				/>,
				<input
					className="form-check-input"
					type="checkbox"
					title="Exclude this pick from counter offers"
					checked={pick.excluded}
					onChange={() => {
						handleToggle(userOrOther, "pick", "exclude", pick.dpid);
					}}
				/>,
				{
					value: <SafeHtml dirty={pick.desc} />,
					searchValue: pick.desc,
					sortValue: pick.desc,
				},
			],
			classNames: {
				"table-danger": pick.excluded && !pick.included,
				"table-success": pick.included,
			},
		};
	});
};

const pickCols = getCols(["", "X", "Draft Picks"], {
	"": {
		sortSequence: [],
	},
	"Draft Picks": {
		width: "100%",
	},
});

const AssetList = ({
	challengeNoRatings,
	tradeContexts,
	handleBulk,
	handleToggle,
	numDraftRounds,
	picks,
	roster,
	stats,
	userOrOther,
}: {
	challengeNoRatings: boolean;
	tradeContexts: TradeProps["tradeContexts"];
	handleBulk: HandleBulk;
	handleToggle: HandleToggle;
	numDraftRounds: number;
	picks: Picks;
	roster: Roster;
	stats: Stats;
	userOrOther: UserOrOther;
}) => {
	const playerCols = getCols(
		[
			"",
			"X",
			"Name",
			"Traits / Mood",
			"Pos",
			"Age",
			"Ovr",
			"Pot",
			"Contract",
			"Exp",
			...stats.map((stat) => `stat:${stat}`),
		],
		{
			"": {
				sortSequence: [],
				noSearch: true,
			},
			"Traits / Mood": {
				title: "Traits / Mood",
				desc: "Personality and satisfaction with the current organization",
			},
			Name: {
				width: "100%",
			},
		},
	);

	// Main roster, prospects and the rest of the farm system in separate tabs, so it is always clear where a player comes
	// from and the real prospects aren't lost among ~45 minor leaguers
	const [tab, setTab] = useState<"roster" | "prospects" | "depth">("roster");
	const groups = {
		roster: roster.filter((p) => p.tid !== PLAYER.MINORS),
		prospects: roster.filter((p) => p.tid === PLAYER.MINORS && p.prospect),
		depth: roster.filter((p) => p.tid === PLAYER.MINORS && !p.prospect),
	};
	const tabs = [
		{ key: "roster", label: "Roster" },
		{ key: "prospects", label: "Prospects" },
		{ key: "depth", label: "Minors depth" },
	] as const;

	const playerRows = genPlayerRows(
		groups[tab],
		handleToggle,
		userOrOther,
		stats,
		challengeNoRatings,
		tradeContexts,
	);
	const pickRows = genPickRows(picks, handleToggle, userOrOther);

	const userOrOtherKey = `${userOrOther[0]!.toUpperCase()}${userOrOther.slice(
		1,
	)}`;

	return (
		<div className="row">
			<div className="col-xl-9">
				<ul className="nav nav-tabs mb-2">
					{tabs.map(({ key, label }) => {
						const numIncluded = groups[key].filter((p) => p.included).length;
						return (
							<li className="nav-item" key={key}>
								<button
									className={clsx("nav-link", { active: tab === key })}
									onClick={() => setTab(key)}
								>
									{label} ({groups[key].length})
									{numIncluded > 0 ? (
										<span className="badge bg-success ms-1">{numIncluded}</span>
									) : null}
								</button>
							</li>
						);
					})}
				</ul>
				{tab !== "roster" ? (
					<p className="small text-body-secondary">
						Minor leaguers stay in the minors with their new team, and they
						don't count against the roster limit or the payroll.
						{tab === "depth"
							? " Depth players are worth little on their own, but the first three in a trade help close a deal that is almost there."
							: null}
					</p>
				) : null}
				<Dropdown className="d-inline-block mb-2">
					<Dropdown.Toggle
						variant="secondary"
						id={`trade-players-bulk-${userOrOtherKey}`}
						className="btn-sm"
					>
						Bulk exclude
					</Dropdown.Toggle>
					<Dropdown.Menu>
						<Dropdown.Item
							onClick={() => {
								handleBulk(
									"check",
									userOrOther,
									"player",
									undefined,
									groups[tab].map((p) => p.pid),
								);
							}}
						>
							Make all untradeable
						</Dropdown.Item>
						<Dropdown.Item
							onClick={() => {
								handleBulk(
									"clear",
									userOrOther,
									"player",
									undefined,
									groups[tab].map((p) => p.pid),
								);
							}}
						>
							Clear all untradeable
						</Dropdown.Item>
					</Dropdown.Menu>
				</Dropdown>
				<DataTable
					className="datatable-negative-margin-top"
					cols={playerCols}
					defaultSort={[6, "desc"]}
					defaultStickyCols={window.mobile ? 2 : 3}
					name={`Trade:${userOrOtherKey}${tab === "roster" ? "" : ":Minors"}`}
					rows={playerRows}
				/>
			</div>
			<div className="col-xl-3">
				<Dropdown className="d-inline-block mb-2">
					<Dropdown.Toggle
						variant="secondary"
						id={`trade-picks-bulk-${userOrOtherKey}`}
						className="btn-sm"
					>
						Bulk exclude
					</Dropdown.Toggle>
					<Dropdown.Menu>
						<Dropdown.Item
							onClick={() => {
								handleBulk("check", userOrOther, "pick");
							}}
						>
							Make all untradeable
						</Dropdown.Item>
						{range(numDraftRounds).map((i) => (
							<Dropdown.Item
								key={i}
								onClick={() => {
									handleBulk("check", userOrOther, "pick", i + 1);
								}}
							>
								Make all {helpers.ordinal(i + 1)} round picks untradeable
							</Dropdown.Item>
						))}
						<Dropdown.Item
							onClick={() => {
								handleBulk("clear", userOrOther, "pick");
							}}
						>
							Clear all untradeable
						</Dropdown.Item>
					</Dropdown.Menu>
				</Dropdown>
				<DataTable
					className="datatable-negative-margin-top"
					cols={pickCols}
					defaultSort={[1, "asc"]}
					hideAllControls
					name={`Trade:Picks:${userOrOtherKey}`}
					rows={pickRows}
				/>
			</div>
		</div>
	);
};

export default AssetList;
